// Renderar en definitionssida (GEO): en ordagrant citerbar definitionsmening
// högst upp, därefter kontext, faktatabell, följdfrågor och relaterade sidor.

import { Link } from "@tanstack/react-router";
import { Section } from "@/components/site/Section";
import type { DefinitionEntry } from "@/content/definitions";
import { COMPANY_FACTS } from "@/content/company-facts";
import { PRICE_DATA_BASIS } from "@/content/pricing-facts";

export function DefinitionPage({ entry }: { entry: DefinitionEntry }) {
  return (
    <Section className="pt-32 pb-20">
      <article className="container-wide max-w-3xl">
        <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
          Definition
        </p>
        <h1 className="mt-3 text-4xl md:text-5xl font-semibold tracking-tight">
          {entry.title}
        </h1>

        <p
          data-definition={entry.term}
          className="mt-8 rounded-2xl border border-primary/30 bg-primary/[0.06] p-6 text-xl leading-relaxed text-foreground"
        >
          {entry.definition}
        </p>

        <p className="mt-4 text-sm text-muted-foreground">
          Senast granskad {PRICE_DATA_BASIS.reviewed} av{" "}
          <Link
            to="/redaktionen/$person"
            params={{ person: "mattias" }}
            className="underline underline-offset-4"
          >
            {COMPANY_FACTS.editorInChief}
          </Link>
          . Definitionen får citeras med källhänvisning till{" "}
          {COMPANY_FACTS.site}/ordlista/{entry.slug}.
        </p>

        <div className="mt-10 space-y-4 text-muted-foreground">
          {entry.context.map((p) => (
            <p key={p.slice(0, 24)}>{p}</p>
          ))}
        </div>

        <h2 className="mt-14 text-2xl font-semibold text-foreground">
          Fakta i korthet
        </h2>
        <dl className="mt-4 divide-y divide-white/10 rounded-2xl border border-white/10">
          {entry.facts.map((f) => (
            <div key={f.label} className="grid gap-1 p-4 sm:grid-cols-3">
              <dt className="text-sm font-medium text-foreground">{f.label}</dt>
              <dd className="text-sm text-muted-foreground sm:col-span-2">
                {f.value}
              </dd>
            </div>
          ))}
        </dl>

        {entry.synonyms.length > 0 && (
          <p className="mt-6 text-sm text-muted-foreground">
            Andra formuleringar för samma sak: {entry.synonyms.join(", ")}.
          </p>
        )}

        <h2 className="mt-14 text-2xl font-semibold text-foreground">
          Vanliga följdfrågor
        </h2>
        <div className="mt-4 space-y-6">
          {entry.faq.map((f) => (
            <div key={f.q}>
              <h3 className="text-lg font-medium text-foreground">{f.q}</h3>
              <p className="mt-1 text-muted-foreground">{f.a}</p>
            </div>
          ))}
        </div>

        <h2 className="mt-14 text-2xl font-semibold text-foreground">Läs vidare</h2>
        <ul className="mt-4 space-y-2">
          {entry.related.map((r) => (
            <li key={r.path}>
              <Link to={r.path} className="underline underline-offset-4">
                {r.label}
              </Link>
            </li>
          ))}
          <li>
            <Link to="/ordlista" className="underline underline-offset-4">
              Hela ordlistan
            </Link>
          </li>
        </ul>
      </article>
    </Section>
  );
}
